import { useUnitStore } from "@/lib/unit";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { navigate, useSearch, useTitle } from "@/lib/router";
import { addDaysIso, fmtDate, todayIso } from "@/lib/format";
import { useLookups } from "@/lib/catalog";
import { useRememberedSearch } from "@/lib/prefs";
import { FilterNotice } from "@/ui/filter-notice";
import { AreaSelect, Card, ErrorBox, PageHeader, Tabs, TypeSelect, UnitSelect, UserSelect } from "@/ui/kit";
import type { ActivityRow } from "./ActivityPages";
import { NewActivityModal } from "./ActivityPages";

type Mode = "dia" | "semana" | "mes";
const WD = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

function initials(name: string): string {
  return name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

function mondayOf(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`);
  const wd = (d.getUTCDay() + 6) % 7;
  return addDaysIso(iso, -wd);
}

interface Ev { a: ActivityRow; date: string; kind: "fatal" | "hearing" | "normal" }

/** Uma atividade pode aparecer em mais de um dia: planejada, interno e fatal. */
function eventsOf(a: ActivityRow, hearingTypeIds: Set<string>): Ev[] {
  const out: Ev[] = [];
  const hearing = !!a.typeId && hearingTypeIds.has(a.typeId);
  if (a.plannedAt) out.push({ a, date: a.plannedAt.slice(0, 10), kind: hearing ? "hearing" : "normal" });
  if (a.fatalDueAt) out.push({ a, date: a.fatalDueAt.slice(0, 10), kind: "fatal" });
  if (a.internalDueAt && !a.fatalDueAt && !a.plannedAt) out.push({ a, date: a.internalDueAt.slice(0, 10), kind: "normal" });
  return out;
}

export function AgendaPage(): JSX.Element {
  useTitle("Agenda");
  const [search, set] = useSearch();
  const mode = (search.get("modo") as Mode) || "semana";
  const ref = search.get("data") ?? todayIso();
  // "minha": o que é meu (responsável, revisor ou participante).
  // "geral": tudo o que minhas permissões deixam ver — equipe, unidade, escritório.
  const scope = (search.get("ver") as "minha" | "geral") || "minha";
  const storeUnit = useUnitStore((x) => x.unitId);
  const f = { resp: search.get("resp") ?? "", unit: search.get("unidade") ?? storeUnit, area: search.get("area") ?? "", type: search.get("tipo") ?? "" };
  const { me, catalog, userName, unitName, areaName, typeOf } = useLookups();
  // Lembra modo (semana/mês), escopo e filtros. A data visitada NÃO: a agenda abre sempre em hoje.
  const remembered = useRememberedSearch("agenda", ["modo", "ver", "resp", "unidade", "area", "tipo", "concluidas"]);
  const [creating, setCreating] = useState(false);

  let from = ref, to = ref;
  if (mode === "semana") { from = mondayOf(ref); to = addDaysIso(from, 6); }
  if (mode === "mes") {
    const first = `${ref.slice(0, 7)}-01`;
    from = mondayOf(first);
    to = addDaysIso(from, 41);
  }
  const { data, error } = useQuery({
    queryKey: ["agenda", from, to, scope, f],
    queryFn: () => api.get<{ items: ActivityRow[]; total: number }>(`/atividades${qs({
      view: "todas", from, to, includeClosed: true, take: 1000,
      mine: scope === "minha" || undefined,
      responsibleUserId: scope === "geral" ? f.resp || undefined : undefined,
      unitId: scope === "geral" ? f.unit || undefined : undefined,
      areaId: f.area || undefined, typeId: f.type || undefined,
    })}`),
    enabled: !!me,
  });
  const hearingIds = new Set((catalog?.activityTypes ?? []).filter((t) => t.key === "audiencia").map((t) => t.id));
  const showDone = search.get("concluidas") === "1";
  const evs = (data?.items ?? []).filter((a) => showDone || !["concluida", "cancelada"].includes(a.state)).flatMap((a) => eventsOf(a, hearingIds));
  const byDay = new Map<string, Ev[]>();
  for (const e of evs) byDay.set(e.date, [...(byDay.get(e.date) ?? []), e]);
  const today = todayIso();

  const step = (dir: 1 | -1) => {
    if (mode === "dia") set({ data: addDaysIso(ref, dir) });
    else if (mode === "semana") set({ data: addDaysIso(ref, 7 * dir) });
    else { const d = new Date(`${ref.slice(0, 7)}-15T12:00:00Z`); d.setUTCMonth(d.getUTCMonth() + dir); set({ data: d.toISOString().slice(0, 10) }); }
  };
  const title = mode === "mes"
    ? new Date(`${ref.slice(0, 7)}-15T12:00:00Z`).toLocaleDateString("pt-BR", { month: "long", year: "numeric" })
    : mode === "semana" ? `${fmtDate(from)} a ${fmtDate(to)}` : new Date(`${ref}T12:00:00Z`).toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" });

  const evButton = (e: Ev, i: number) => (
    <button key={`${e.a.id}-${e.kind}-${i}`} className={`cal-ev ${e.kind === "fatal" ? "fatal" : e.kind === "hearing" ? "hearing" : ""} ${["concluida", "cancelada"].includes(e.a.state) ? "done" : ""} ${e.a.state === "aguardando_dependencia" ? "waiting" : ""}`}
      title={`${e.a.state === "aguardando_dependencia" ? "[Aguardando etapa anterior] " : ""}${e.a.title} — ${userName(e.a.responsibleUserId)}`} onClick={() => navigate(`/atividades/${e.a.id}`)}>
      {e.a.state === "aguardando_dependencia" ? "⏳ " : ""}{e.kind === "fatal" ? "⚑ " : e.kind === "hearing" ? "⚖ " : ""}{e.a.title}
      {scope === "geral" && e.a.responsibleUserId && <span className="cal-who"> · {initials(userName(e.a.responsibleUserId))}</span>}
    </button>
  );

  const AG: Record<string, (v: string) => [string, string?]> = {
    resp: (v) => ["Responsável", userName(v)], unidade: (v) => ["Unidade", unitName(v)], area: (v) => ["Área", areaName(v)],
    tipo: (v) => ["Tipo", typeOf(v)?.name ?? "tipo"], concluidas: () => ["Mostrando concluídas"],
  };
  const agItems = remembered.active.filter((k) => AG[k]).map((k) => { const [label, value] = AG[k]!(remembered.values[k]!); return { label, value }; });
  const notToday = today < from || today > to ? `A agenda está mostrando ${fmtDate(from)} a ${fmtDate(to)} — não inclui hoje.` : null;

  return (
    <div>
      <FilterNotice items={agItems} restored={remembered.restored} stale={notToday}
        onClear={() => { if (agItems.length) remembered.clear(); set({ data: null }); }} />
      <PageHeader title="Agenda" subtitle={<><span style={{ textTransform: "capitalize" }}>{title}</span> <span className="muted small" style={{ marginLeft: 8 }}><span className="legend-waiting" /> ⏳ aguardando etapa anterior</span></>}
        actions={<>
          <button className="btn" onClick={() => step(-1)}>‹</button>
          <button className="btn" onClick={() => set({ data: null })}>Hoje</button>
          <button className="btn" onClick={() => step(1)}>›</button>
          <button className="btn primary" onClick={() => setCreating(true)}>Nova atividade</button>
        </>} />
      <div className="toolbar">
        <div className="seg">
          <button className={scope === "minha" ? "active" : ""} onClick={() => set({ ver: null })}>Minha agenda</button>
          <button className={scope === "geral" ? "active" : ""} onClick={() => set({ ver: "geral" })}>Agenda geral</button>
        </div>
        <Tabs<Mode> value={mode} onChange={(m) => set({ modo: m })} tabs={[{ key: "dia", label: "Dia" }, { key: "semana", label: "Semana" }, { key: "mes", label: "Mês" }]} />
        <span className="spacer" />
        <label className="row nowrap small"><input type="checkbox" checked={showDone} onChange={(e) => set({ concluidas: e.target.checked ? "1" : null })} /> mostrar concluídas</label>
        <span className="muted small">⚑ prazo fatal · ⚖ audiência</span>
      </div>
      <div className="toolbar">
        {scope === "geral" && <div style={{ width: 200 }}><UserSelect value={f.resp} onChange={(v) => set({ resp: v })} emptyLabel="Todas as pessoas" /></div>}
        {scope === "geral" && <div style={{ width: 170 }}><UnitSelect value={f.unit || null} onChange={(v) => { useUnitStore.getState().set(v ?? ""); set({ unidade: null }); }} allowEmpty /></div>}
        <div style={{ width: 170 }}><AreaSelect value={f.area} onChange={(v) => set({ area: v })} allowEmpty /></div>
        <div style={{ width: 190 }}><TypeSelect value={f.type} onChange={(v) => set({ tipo: v })} /></div>
        {scope === "geral" && <span className="muted small">Mostra tudo o que suas permissões alcançam (equipe, unidade ou escritório).</span>}
        {data && data.items.length >= 1000 && <span className="text-warn small">Muitos compromissos: filtre por pessoa, unidade ou área.</span>}
      </div>
      <ErrorBox error={error} />

      {mode === "mes" && (
        <div className="cal">
          {WD.map((d) => <div key={d} className="cal-head">{d}</div>)}
          {Array.from({ length: 42 }, (_, i) => addDaysIso(from, i)).map((d) => (
            <div key={d} className={`cal-day ${d.slice(0, 7) !== ref.slice(0, 7) ? "other" : ""} ${d === today ? "today" : ""}`}>
              <div className="cal-num">{Number(d.slice(8))}</div>
              {(byDay.get(d) ?? []).slice(0, 5).map(evButton)}
              {(byDay.get(d)?.length ?? 0) > 5 && <button className="link-btn small" onClick={() => set({ modo: "dia", data: d })}>+{byDay.get(d)!.length - 5}</button>}
            </div>
          ))}
        </div>
      )}

      {mode === "semana" && (
        <div className="week">
          {Array.from({ length: 7 }, (_, i) => addDaysIso(from, i)).map((d, i) => (
            <div key={d} className={`week-col ${d === today ? "today" : ""}`}>
              <h4><button className="link-btn inline" onClick={() => set({ modo: "dia", data: d })}>{WD[i]} {fmtDate(d).slice(0, 5)}</button></h4>
              {(byDay.get(d) ?? []).map(evButton)}
            </div>
          ))}
        </div>
      )}

      {mode === "dia" && (
        <div className="grid cols-3">
          {(["hearing", "fatal", "normal"] as const).map((k) => (
            <Card key={k} title={k === "hearing" ? "Audiências" : k === "fatal" ? "Prazos fatais" : "Demais compromissos"}>
              {(byDay.get(ref) ?? []).filter((e) => e.kind === k).map((e) => (
                <div key={e.a.id + k} className={`row between ${e.a.state === "aguardando_dependencia" ? "waiting" : ""}`} style={{ padding: "6px 0", borderBottom: "1px solid var(--border-l)" }} title={e.a.state === "aguardando_dependencia" ? "Aguardando etapa anterior" : undefined}>
                  <a href={`/atividades/${e.a.id}`} onClick={(ev) => { ev.preventDefault(); navigate(`/atividades/${e.a.id}`); }}>#{e.a.number} {e.a.title}</a>
                  <span className="muted small">{userName(e.a.responsibleUserId)}</span>
                </div>
              ))}
              {!(byDay.get(ref) ?? []).some((e) => e.kind === k) && <div className="muted small">Nada.</div>}
            </Card>
          ))}
        </div>
      )}
      {creating && <NewActivityModal onClose={() => setCreating(false)} />}
    </div>
  );
}
